import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import FeatureCard from "../../FeatureCard";

const OVERSCAN = 3;

const FeatureVirtualList = forwardRef(
  (
    { features, style = {}, children, ...props },
    ref // this is the forwarded ref from parent
  ) => {
    const containerRef = useRef();

    // Expose the scrollable container to the parent ref
    useEffect(() => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(containerRef.current);
      } else {
        ref.current = containerRef.current;
      }
    }, [ref]);

    const [scrollTop, setScrollTop] = useState(0);
    const [itemHeights, setItemHeights] = useState({});
    const [containerHeight, setContainerHeight] = useState(0);
    const itemHeightsRef = useRef({});

    // Track refs for measuring
    const itemRefs = useRef({});

    // Measure container height on mount/resize
    useEffect(() => {
      const measure = () => {
        if (containerRef.current) {
          setContainerHeight(containerRef.current.clientHeight);
        }
      };
      measure();
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }, []);

    // Measure item heights after render
    useEffect(() => {
      let changed = false;
      const newHeights = { ...itemHeightsRef.current };
      features.forEach((feature) => {
        const objectId = feature.getObjectId();
        const el = itemRefs.current[objectId];
        if (el) {
          const h = el.offsetHeight;
          if (newHeights[objectId] !== h) {
            newHeights[objectId] = h;
            changed = true;
          }
        }
      });
      if (changed) {
        itemHeightsRef.current = newHeights;
        setItemHeights({ ...newHeights });
      }
    }, [features, scrollTop]);

    // Calculate visible window
    let y = 0,
      start = 0,
      end = features.length;
    for (let i = 0; i < features.length; i++) {
      const objectId = features[i].getObjectId();
      const h = itemHeights[objectId] || 100;
      if (y + h > scrollTop) {
        start = Math.max(0, i - OVERSCAN);
        break;
      }
      y += h;
    }
    y = 0;
    for (let i = 0; i < features.length; i++) {
      const objectId = features[i].getObjectId();
      y += itemHeights[objectId] || 100;
      if (y > scrollTop + containerHeight) {
        end = Math.min(features.length, i + OVERSCAN);
        break;
      }
    }

    // Calculate spacer heights
    const topSpacer = Array.from({ length: start }).reduce((acc, _, idx) => {
      const objectId = features[idx].getObjectId();
      return acc + (itemHeights[objectId] || 100);
    }, 0);
    const bottomSpacer = Array.from({ length: features.length - end }).reduce(
      (acc, _, idx) => {
        const objectId = features[end + idx]?.getObjectId();
        return acc + (itemHeights[objectId] || 100);
      },
      0
    );

    const handleScroll = useCallback((e) => {
      setScrollTop(e.target.scrollTop);
    }, []);

    // Debounced forceRemeasure to avoid excessive re-measuring
    const debounce = (fn, delay) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
      };
    };

    const forceRemeasure = useCallback(
      debounce(() => {
        let changed = false;
        const newHeights = { ...itemHeightsRef.current };
        features.forEach((feature) => {
          const objectId = feature.getObjectId();
          const el = itemRefs.current[objectId];
          if (el) {
            const h = el.offsetHeight;
            if (newHeights[objectId] !== h) {
              newHeights[objectId] = h;
              changed = true;
            }
          }
        });
        if (changed) {
          itemHeightsRef.current = newHeights;
          setItemHeights({ ...newHeights });
        }
      }, 100),
      [features]
    );

    return (
      <div
        ref={containerRef}
        style={{ overflowY: "auto", height: "100%", ...style }}
        onScroll={handleScroll}
        {...props}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {topSpacer > 0 && <div style={{ height: topSpacer }} />}
          {features.slice(start, end).map((feature) => {
            const objectId = feature.getObjectId();
            const layer = feature.sourceLayer || feature.layer;
            return (
              <div
                key={objectId}
                ref={(el) => (itemRefs.current[objectId] = el)}
              >
                <FeatureCard
                  layer={layer}
                  feature={feature}
                  onImageLoad={forceRemeasure}
                />
              </div>
            );
          })}
          {bottomSpacer > 0 && <div style={{ height: bottomSpacer }} />}
        </div>
        {children}
      </div>
    );
  }
);

export default FeatureVirtualList;
